/**
 * Tipos compartilhados pelos passos do ImportCustomersWizard.
 *
 * Regras de obrigatoriedade (cliente):
 *   - nome      → obrigatório
 *   - telefone  → obrigatório
 *   - cpf OU cnpj → pelo menos UM precisa estar mapeado
 *
 * A obrigatoriedade "cpf XOR cnpj" é validada no Step2Mapping (frontend) e
 * reforçada server-side em validate_customer_import_job.
 *
 * RELEASE:
 *   2026-08-23 v2.0.0 - Campos de endereço completo (logradouro/numero/complemento/
 *     bairro), nome_fantasia e data_nascimento passam a ser mapeáveis. `endereco`
 *     mantido como alias legado de logradouro.
 *   2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */

import type { CSVRow } from '@/lib/csv-utils';

export interface CustomerFieldDef {
  key: string;
  label: string;
  required?: boolean;
  /** Campo que satisfaz a regra "cpf OU cnpj" (pelo menos um precisa existir). */
  identityGroup?: 'pfpj';
}

/**
 * Lista de campos suportados no mapeamento (csv_column -> customer_field).
 * As chaves precisam bater com as lidas pelo worker process_customer_import_batch.
 */
export const CUSTOMER_FIELDS: CustomerFieldDef[] = [
  { key: 'nome', label: 'Nome completo (obrigatório)', required: true },
  { key: 'telefone', label: 'Telefone (obrigatório)', required: true },
  { key: 'cpf', label: 'CPF (obrigatório: CPF OU CNPJ)', identityGroup: 'pfpj' },
  { key: 'cnpj', label: 'CNPJ (obrigatório: CPF OU CNPJ)', identityGroup: 'pfpj' },
  { key: 'razao_social', label: 'Razão Social (PJ)' },
  { key: 'nome_fantasia', label: 'Nome Fantasia (PJ)' },
  { key: 'email', label: 'E-mail' },
  { key: 'data_nascimento', label: 'Data de Nascimento (DD/MM/AAAA)' },
  { key: 'cep', label: 'CEP' },
  { key: 'logradouro', label: 'Logradouro (rua/avenida)' },
  { key: 'numero', label: 'Número' },
  { key: 'complemento', label: 'Complemento' },
  { key: 'bairro', label: 'Bairro' },
  { key: 'cidade', label: 'Cidade' },
  { key: 'estado', label: 'Estado (UF)' },
  { key: 'endereco', label: 'Endereço (legado — vira Logradouro)' },
  { key: 'observacao', label: 'Observação' },
];

/** Heurística de autodetect de cabeçalhos do CSV (lower-case, trim). */
export const AUTODETECT: Record<string, string> = {
  nome: 'nome', name: 'nome', 'full_name': 'nome', 'nome_completo': 'nome',
  telefone: 'telefone', phone: 'telefone', celular: 'telefone', whatsapp: 'telefone',
  cpf: 'cpf',
  cnpj: 'cnpj',
  'razao_social': 'razao_social', 'razão_social': 'razao_social', empresa: 'razao_social', company: 'razao_social',
  'nome_fantasia': 'nome_fantasia', fantasia: 'nome_fantasia',
  email: 'email', 'e-mail': 'email', 'e_mail': 'email',
  'data_nascimento': 'data_nascimento', nascimento: 'data_nascimento',
  'dt_nascimento': 'data_nascimento', birthdate: 'data_nascimento',
  cidade: 'cidade', city: 'cidade',
  estado: 'estado', uf: 'estado', state: 'estado',
  // Logradouro absorve os cabeçalhos livres de endereço (inclui o legado "endereco").
  logradouro: 'logradouro', endereco: 'logradouro', endereço: 'logradouro',
  address: 'logradouro', rua: 'logradouro',
  numero: 'numero', número: 'numero', 'nº': 'numero', num: 'numero',
  complemento: 'complemento', compl: 'complemento',
  bairro: 'bairro', neighborhood: 'bairro',
  cep: 'cep', 'zip_code': 'cep', zipcode: 'cep',
  observacao: 'observacao', observação: 'observacao', notes: 'observacao', obs: 'observacao',
};


/**
 * Cabeçalhos sensíveis sempre IGNORADOS pelo backend.
 * Evita criar clientes com dados financeiros forjados via CSV.
 */
export const SENSITIVE_HEADERS = [
  'credit_limit', 'limite_credito',
  'status', 'situacao', 'situação',
  'tenant_id', 'user_id', 'workspace_id',
];

export type { CSVRow };

export interface ValidationCounters {
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  blacklisted_count: number;
}
